import React from 'react';
import { Bar, BarChart, CartesianGrid, Cell, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { AirflowDagRunState } from '../../types';
import { formatDateTime, formatDurationSeconds } from '../../utils';

export type ChartRun = {
    runId: string;
    label?: string;
    durationSec: number;
    state: AirflowDagRunState;
};

const STATE_FILL: Record<AirflowDagRunState, string> = {
    success: '#08c46a',
    failed: '#ff4d4f',
    running: '#1890ff',
    queued: '#fcad01',
};

type RunsDurationChartProps = {
    runs: ChartRun[];
    onRunClick?: (runId: string) => void;
};

/*
 * Custom tooltip for the duration chart, showing state, duration, date, and truncated run ID
 */
function DurationTooltip({ active, payload }: any) {
    if (!active || !payload?.length) return null;
    const run: ChartRun = payload[0].payload;
    return (
        <div style={{ background: '#212233', border: '1px solid #3e4161', borderRadius: 6, padding: 8, fontSize: 12, color: '#e1e1e6' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ width: 8, height: 8, borderRadius: '50%', background: STATE_FILL[run.state] ?? '#666a8d' }} />
                {run.state[0].toUpperCase() + run.state.slice(1)}
            </div>
            <div>{formatDurationSeconds(run.durationSec)}</div>
            <div>{formatDateTime(run.label)}</div>
            <div style={{ fontFamily: 'monospace', color: '#666a8d' }}>{run.runId.slice(0, 24)}</div>
        </div>
    );
}

/*
 * Bar chart of recent run durations colored by state, click-through to the run's detail page
 */
export function RunsDurationChart({ runs, onRunClick }: RunsDurationChartProps) {
    if (runs.length === 0) {
        return <div style={{ color: '#666a8d', padding: 16 }}>No recent runs to chart.</div>;
    }

    const stateCounts = runs.reduce<Record<string, number>>((acc, run) => {
        acc[run.state] = (acc[run.state] ?? 0) + 1;
        return acc;
    }, {});

    return (
        <div>
            <ResponsiveContainer width="100%" height={260}>
                <BarChart
                    data={runs}
                    margin={{ top: 8, right: 8, bottom: 8, left: 8 }}
                    onClick={(data) => {
                        const runId = (data as any)?.activePayload?.[0]?.payload?.runId;
                        if (runId) onRunClick?.(runId);
                    }}
                    style={onRunClick ? { cursor: 'pointer' } : undefined}
                >
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#3e4161" strokeOpacity={0.7} />
                    <XAxis dataKey="runId" hide />
                    <YAxis tickFormatter={(v) => formatDurationSeconds(v)} width={60} tick={{ fontSize: 11, fill: '#666a8d' }} axisLine={false} tickLine={false} />
                    <Tooltip content={<DurationTooltip />} cursor={{ fill: '#3e4161', opacity: 0.5 }} />
                    <Bar dataKey="durationSec" radius={[4, 4, 0, 0]} maxBarSize={28} isAnimationActive={false}>
                        {runs.map((run) => (
                            <Cell key={run.runId} fill={STATE_FILL[run.state] ?? '#666a8d'} />
                        ))}
                    </Bar>
                </BarChart>
            </ResponsiveContainer>
            <div style={{ display: 'flex', gap: 16, marginTop: 8 }}>
                {Object.entries(stateCounts).map(([state, count]) => (
                    <div key={state} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#666a8d' }}>
                        <span style={{ width: 10, height: 10, borderRadius: 2, background: STATE_FILL[state as AirflowDagRunState] ?? '#666a8d' }} />
                        {`${state} (${count})`}
                    </div>
                ))}
            </div>
        </div>
    );
}
